/**
 * AdminFilterHome screen styles - V3 Design
 */

import { StyleSheet, Dimensions } from 'react-native'
import { heightPercentageToDP as hp, widthPercentageToDP as wp } from 'react-native-responsive-screen';
import { isWeb } from '../../util/platform';

import { fonts } from '../fonts';
import AppStyles from '../../AppStyles';

const screenWidth = Dimensions.get('window').width;
let scale = 1;
if (screenWidth < 800 && isWeb()) {
  scale = 0.7;
}

const styles = StyleSheet.create({
  // V3: Light background for report list area
  orangeContainer: {
    flex: 1,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    backgroundColor: '#F8F9FA',
    paddingVertical: 8,
    paddingHorizontal: 8,
    marginTop: 4,
  },
  // V3: Clean search box with subtle styling
  textboxContainer: {
    flexDirection: 'row',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    width: '100%',
    height: screenWidth < 500 ? 44 : hp(6),
    marginTop: 2,
    marginBottom: 4,
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
  },
  textboxIconContainer: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: 50,
  },
  textboxResetSearch: {
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    width: screenWidth < 500 ? 32 : 44,
  },
  textboxText: {
    flex: 4,
    fontFamily: fonts.POPPINS.regular,
    fontSize: 18 * scale,
    color: AppStyles.colorSet.textPrimary,
    marginLeft: 12,
    width: '100%',
    paddingVertical: 8,
    textAlignVertical: 'center',
  },
  // V3: Email CSV button
  emailCSVButton: {
    cursor: 'pointer',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    alignSelf: 'center',
    height: hp(4),
    paddingHorizontal: 16,
  },
  emailCSVButtonMobile: {
    cursor: 'pointer',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    alignSelf: 'flex-start',
    height: screenWidth < 500 ? 32 : hp(4),
    paddingHorizontal: 12,
    marginBottom: 2,
    marginTop: 4,
    marginLeft: 4,
  },
  // V3: Pill-style time filter chips (compact on mobile)
  Chip: {
    marginHorizontal: wp(0),
    backgroundColor: AppStyles.colorSet.cardBackground,
    justifyContent: 'center',
    flexDirection: 'row',
    flex: 1,
    borderRadius: 20,
    borderColor: AppStyles.colorSet.inputBorder,
    borderWidth: 1.5,
    paddingVertical: screenWidth < 500 ? 6 : 8,
    paddingHorizontal: screenWidth < 500 ? 10 : 14,
  },
  ChipSelected: {
    marginHorizontal: wp(0),
    backgroundColor: AppStyles.colorSet.brandPrimary,
    justifyContent: 'center',
    flexDirection: 'row',
    flex: 1,
    borderRadius: 20,
    borderColor: '#D4A000',
    borderWidth: 2,
    paddingVertical: screenWidth < 500 ? 6 : 8,
    paddingHorizontal: screenWidth < 500 ? 10 : 14,
  },
  // Ticket view chips - primary color scheme
  TicketChip: {
    marginHorizontal: wp(0),
    backgroundColor: AppStyles.colorSet.cardBackground,
    justifyContent: 'center',
    flexDirection: 'row',
    flex: 1,
    borderRadius: 20,
    borderColor: AppStyles.colorSet.inputBorder,
    borderWidth: 1.5,
    paddingVertical: screenWidth < 500 ? 6 : 8,
    paddingHorizontal: screenWidth < 500 ? 10 : 14,
  },
  TicketChipSelected: {
    marginHorizontal: wp(0),
    backgroundColor: AppStyles.colorSet.brandPrimary,
    justifyContent: 'center',
    flexDirection: 'row',
    flex: 1,
    borderRadius: 20,
    borderColor: '#D4A000',
    borderWidth: 2,
    paddingVertical: screenWidth < 500 ? 6 : 8,
    paddingHorizontal: screenWidth < 500 ? 10 : 14,
  },
  // Web hover/focus feedback for the (unselected) ticket-view filter chips
  TicketChipHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: 'rgba(255, 196, 81, 0.08)',
  },
  ChipText: {
    marginHorizontal: screenWidth < 500 ? 2 : wp(1),
  },
  chip_container: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    paddingVertical: screenWidth < 500 ? 4 : hp(1),
    paddingHorizontal: wp(1),
    gap: 6,
  },
  // Toggle row for view-specific options
  toggleRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    columnGap: 16,
    paddingVertical: 8,
    paddingHorizontal: wp(1),
    backgroundColor: AppStyles.colorSet.whiteColor,
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.inputBorder,
  },
  toggleButtonActive: {
    backgroundColor: `${AppStyles.colorSet.brandPrimary}25`,
    borderColor: AppStyles.colorSet.brandPrimary,
  },
  // Web hover/focus feedback for filter toggle chips
  toggleButtonHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
  },
  // Web hover/focus feedback for the search box icon buttons
  textboxIconHovered: {
    backgroundColor: '#E5E7EB',
  },
  textboxResetHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: 11,
  },
  // V3: Filter section
  filterContainer: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    paddingHorizontal: wp(1),
    paddingVertical: hp(1),
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.cardBorder,
  },
  // Organization indicator
  orgIndicatorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 16,
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.cardBorder,
  },
  orgIndicatorChangeButton: {
    marginLeft: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
});

export default styles;
